import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BookingFieldKey, ColumnMapping, EMPTY_MAPPING, autoMapColumns, isBlankRow, isMappingComplete } from "./bookingFields";
import { ParsedWorkbook, RawRow } from "./parseWorkbook";

/** The wizard's steps. "upload" carries no step indicator — the design numbers only the last three. */
export type BulkBookingStep = "upload" | "select-header" | "match-columns" | "review";

/** The client the import books for. Absent in the client portal's self-service flow. */
export type BulkBookingClient = {
  /** account_uuid — scopes every API call, exactly as the single-booking OBO flow does. */
  accountId: string;
  name: string;
  avatarSrc?: string | null;
};

interface BulkBookingState {
  step: BulkBookingStep;
  client?: BulkBookingClient;
  /** The uploaded file, kept so a later step can name it in an error. */
  file?: File;
  workbook?: ParsedWorkbook;
  /** Index into `workbook.rows` of the row holding the column names. */
  headerRowIndex: number;
  setHeaderRowIndex: (index: number) => void;
  /** Called by step 1 once a file parses. Advances to the header picker. */
  loadWorkbook: (file: File, workbook: ParsedWorkbook) => void;
  goTo: (step: BulkBookingStep) => void;
  /** Header labels from the chosen row. */
  headers: RawRow;
  /** Every row below the chosen header row — the actual bookings, blank lines dropped. */
  dataRows: RawRow[];
  /** Booking field -> index of the file column feeding it. */
  mapping: ColumnMapping;
  setMapping: (field: BookingFieldKey, columnIndex: number | null) => void;
  resetMapping: () => void;
  /** True once every required field is mapped — gates Match Columns' Next. */
  canProceedFromMapping: boolean;
  /** Write an inline edit from the review step back into the underlying cell. */
  editCell: (rowIndex: number, field: BookingFieldKey, value: string) => void;
}

const BulkBookingContext = createContext<BulkBookingState | null>(null);

interface BulkBookingProviderProps extends React.PropsWithChildren {
  client?: BulkBookingClient;
}

export const BulkBookingProvider = ({ client, children }: BulkBookingProviderProps) => {
  const [step, setStep] = useState<BulkBookingStep>("upload");
  const [file, setFile] = useState<File>();
  const [workbook, setWorkbook] = useState<ParsedWorkbook>();
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [mapping, setMappingState] = useState<ColumnMapping>(EMPTY_MAPPING);
  /**
   * Inline edits from the review step, keyed by "rowIndex:field". Held separately rather than
   * mutating the parsed workbook so going Back to an earlier step still shows the original file.
   */
  const [edits, setEdits] = useState<Record<string, string>>({});

  const loadWorkbook = useCallback((nextFile: File, nextWorkbook: ParsedWorkbook) => {
    setFile(nextFile);
    setWorkbook(nextWorkbook);
    // Our own template puts headers in row 1, so pre-select it — most uploads need no change here.
    setHeaderRowIndex(0);
    setStep("select-header");
  }, []);

  const headers = useMemo(() => workbook?.rows[headerRowIndex] ?? [], [workbook, headerRowIndex]);
  // Trailing blank lines are an artefact of how people save spreadsheets, not bookings — dropping
  // them here keeps them out of the row count, the validation calls and the review table.
  const dataRows = useMemo(() => {
    const parsed = (workbook?.rows.slice(headerRowIndex + 1) ?? []).filter((row) => !isBlankRow(row));
    if (Object.keys(edits).length === 0) return parsed;

    // Overlay the review step's edits so everything downstream — re-validation, the import payload —
    // sees the corrected value without the parsed file being mutated.
    return parsed.map((row, rowIndex) => {
      const rowEdits = Object.entries(edits).filter(([key]) => key.startsWith(`${rowIndex}:`));
      if (rowEdits.length === 0) return row;

      const next = [...row];
      rowEdits.forEach(([key, value]) => {
        const field = key.slice(key.indexOf(":") + 1) as BookingFieldKey;
        const column = mapping[field];
        if (column !== null) next[column] = value;
      });
      return next;
    });
  }, [workbook, headerRowIndex, edits, mapping]);

  // Re-run auto-mapping whenever the header row changes — picking a different row means a different
  // set of column names, so any previous mapping is meaningless.
  useEffect(() => {
    setMappingState(headers.length ? autoMapColumns(headers) : EMPTY_MAPPING);
  }, [headers]);

  const setMapping = useCallback((field: BookingFieldKey, columnIndex: number | null) => {
    setMappingState((current) => {
      // A column can only feed one field, so claiming it clears it from wherever it was.
      const next = { ...current };
      if (columnIndex !== null) {
        (Object.keys(next) as BookingFieldKey[]).forEach((key) => {
          if (next[key] === columnIndex) next[key] = null;
        });
      }
      next[field] = columnIndex;
      return next;
    });
  }, []);

  const resetMapping = useCallback(() => setMappingState(EMPTY_MAPPING), []);

  const editCell = useCallback((rowIndex: number, field: BookingFieldKey, value: string) => {
    setEdits((current) => ({ ...current, [`${rowIndex}:${field}`]: value }));
  }, []);

  const value = useMemo(
    () => ({
      step,
      client,
      file,
      workbook,
      headerRowIndex,
      setHeaderRowIndex,
      loadWorkbook,
      goTo: setStep,
      headers,
      dataRows,
      mapping,
      setMapping,
      resetMapping,
      canProceedFromMapping: isMappingComplete(mapping),
      editCell,
    }),
    [step, client, file, workbook, headerRowIndex, loadWorkbook, headers, dataRows, mapping, setMapping, resetMapping, editCell]
  );

  return <BulkBookingContext.Provider value={value}>{children}</BulkBookingContext.Provider>;
};

export const useBulkBooking = () => {
  const context = useContext(BulkBookingContext);
  if (!context) throw new Error("useBulkBooking must be used within a BulkBookingProvider");

  return context;
};
